import { Head, router, usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { productsPath, profilePath, profileStorePath, servicesPath } from "@travily/routes";
import type IndividualsStore from "@travily/types/pages/IndividualsStore";
import { SwooshBackground } from "@ui/components/Swoosh";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { useShare } from "@ui/hooks/use-share";
import { PROFILE_SWOOSH_COLORS } from "@/components/ProfileFrame/ProfileFrame";
import { individualProfileAccount } from "@/components/ProfileFrame/profile-account";
import { ProfileStoreView } from "@/components/ProfileStoreView/ProfileStoreView";
import { useFriendshipActions } from "@/hooks/use-friendship-actions";
import { useNavigateBack } from "@/hooks/use-navigate-back";
import { PageFrame } from "@/layouts/page-frame";
import type { SharedProps } from "@/types";

const IndividualStorePage = ({ individual, products, services, can }: IndividualsStore) => {
  const { share } = useShare();
  const { current_account } = usePage<SharedProps>().props;
  const isOwnProfile = current_account?.handle === individual.handle;
  const onBack = useNavigateBack(profilePath(individual.handle));
  const friendshipActions = useFriendshipActions(individual);

  const handleShareProfile = () => {
    share({
      title: individual.display_name,
      text: t("web.individuals.show.share_text", { name: individual.display_name }),
      url: `${location.origin}${profilePath(individual.handle)}`,
    });
  };

  return (
    <PageFrame
      background={<SwooshBackground className="hidden md:block" color={PROFILE_SWOOSH_COLORS.Individual} />}
      mobileTopBar={isOwnProfile ? undefined : <TopBarMobile onBack={onBack} title={`@${individual.handle}`} />}
    >
      <Head title={t("web.individuals.store.meta_title", { name: individual.display_name })}>
        <meta
          name="description"
          content={t("web.individuals.show.meta_description", { name: individual.display_name })}
        />
      </Head>
      <ProfileStoreView
        account={individualProfileAccount(individual)}
        canCreateListing={can.create_listing}
        onAddProduct={can.create_listing ? () => router.post(productsPath()) : undefined}
        onAddService={can.create_listing ? () => router.post(servicesPath()) : undefined}
        onShareProfile={handleShareProfile}
        products={products}
        services={services}
        tabs={{ profileHref: profilePath(individual.handle), storeHref: profileStorePath(individual.handle) }}
        {...friendshipActions}
      />
    </PageFrame>
  );
};

export default IndividualStorePage;
